import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Typography,
    Grid,
    IconButton,
    Card,
    CardContent,
    Chip,
    Box,
    InputAdornment,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    Alert,
    Switch,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    ArrowBack,
    LocationOn,
    CreditCard,
    Person,
    Business,
    Smartphone,
    AccountBalance,
    Language,
    Search,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CloseIcon from '../CustomSVGIcons/CloseIcon';
import { getExternalAccountInfo, createExternalAccount } from '../../redux/account/actions';
import {
    getCountrySupport,
    getPayoutBanks,
    getStripeConfig,
    verifyPayoutAccount,
    clearVerifyPayoutAccount,
} from '../../redux/payout/actions';
import {
    payoutCountrySupportSelector,
    payoutBanksByKeySelector,
    payoutVerifyResultSelector,
    payoutVerifyingAccountSelector,
} from '../../redux/payout/selectors';
import {
    buildExternalAccountPayload,
    buildVerifyAccountPayload,
    hasValidResidentialAddress,
    isAutomatedRail,
    isManualWireRail,
    isVerifyResultAcceptedForSave,
    methodNeedsResidentialAddress,
    paymentMethodsForCountrySupport,
    payoutBanksQueryForMethod,
    requiresPreSaveAccountVerification,
    shouldLoadPayoutBanksForMethod,
    type ExternalAccountFormData,
} from '../../utils/externalAccountPayoutHelpers';
import {
    canTogglePaystackTestBankMode,
    filterBanksForPayoutEnvironment,
    flutterwaveMomoHint,
    isPaystackTestBankMethod,
    isPaystackTestBankCode,
    isLivePayoutBankEnvironment,
    paystackTestBankHint,
    readPaystackTestBankModePreference,
    shouldRestrictToPaystackTestBanks,
    writePaystackTestBankModePreference,
} from '../../utils/payoutBankEnv';
import { isAdminSelector, effectiveOperatingCountryCodeSelector } from '../../selectors/selectors';
import { countries as allCountries } from '../../utils/countriesList';
import { RootState } from '../../redux/root-reducer';
import { IBankInfo } from '../../redux/account/types';
import { showToastMessage } from '../../utils/AppUtils';
import { ResidentialAddressField } from './ResidentialAddressField';
import { fetchBankNameFromRouting, isValidAbaRoutingNumber } from '../../utils/routingNumberLookup';

function isValidAccountHolderName(s: string): boolean {
    const t = s.trim();
    if (t.length < 2) return false;
    return /^[\p{L}\s\-'.]+$/u.test(t);
}

function isSwiftCodeValid(s: string): boolean {
    const t = s.trim().toUpperCase();
    return /^[A-Z0-9]{8,11}$/.test(t);
}

function isDigitsOnlyString(s: string): boolean {
    return /^\d+$/.test(s);
}

// Country list from shared ISO list (manual wire supported for all)
const countries = allCountries.map(c => ({ code: c.code, name: c.name, flag: c.flag }));

interface PaymentMethod {
    id: string;
    name: string;
    description: string;
    icon: React.ReactNode;
    externalAccountType?: string;
}

function paymentMethodIcon(methodId: string): React.ReactNode {
    switch (methodId) {
        case 'nuban':
            return <CreditCard />;
        case 'mobile_money':
        case 'mobile_money_business':
            return <Smartphone />;
        case 'ach':
        case 'bank_account':
            return <AccountBalance />;
        case 'swift':
        case 'wire':
            return <Language />;
        case 'paypal':
            return <Business />;
        default:
            return <AccountBalance />;
    }
}

interface ExternalAccountModalProps {
    open: boolean;
    onClose: () => void;
}

const ExternalAccountModal: React.FC<ExternalAccountModalProps> = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const { themeStyle, device } = useThemeContext();
    const [step, setStep] = useState<'country' | 'payment' | 'details'>('country');
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
    const [isAnimating, setIsAnimating] = useState(false);
    const [formData, setFormData] = useState<any>({});
    const [countrySearch, setCountrySearch] = useState<string>('');
    const [routingBankName, setRoutingBankName] = useState<string | null>(null);
    const [routingBankLookupLoading, setRoutingBankLookupLoading] = useState(false);
    const routingLookupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Redux state
    const { externalAccountInfo, gettingExternalAccountInfo, creatingExternalAccount, userExternalAccounts } =
        useSelector((state: RootState) => state.account);
    const countrySupport = useSelector(payoutCountrySupportSelector);
    const payoutBanksByKey = useSelector(payoutBanksByKeySelector);
    const verifyResult = useSelector(payoutVerifyResultSelector);
    const verifyingAccount = useSelector(payoutVerifyingAccountSelector);
    const loadingPayoutBanksKey = useSelector((state: RootState) => state.payout.loadingPayoutBanksKey);
    const isAdmin = useSelector(isAdminSelector);
    const effectiveCountryCode = useSelector(effectiveOperatingCountryCodeSelector);
    const showTestBankToggle = canTogglePaystackTestBankMode(isAdmin);
    const [paystackTestBankMode, setPaystackTestBankMode] = useState(() => readPaystackTestBankModePreference(true));

    const testModeOpts = useMemo(
        () => ({
            // Non-admins are always in test mode outside production.
            testModeEnabled: showTestBankToggle ? paystackTestBankMode : !isLivePayoutBankEnvironment(),
        }),
        [showTestBankToggle, paystackTestBankMode],
    );

    // Fetch external account info when modal opens
    useEffect(() => {
        if (open && !externalAccountInfo) {
            dispatch(getExternalAccountInfo());
        }
    }, [open, dispatch, externalAccountInfo]);

    // Track if we just successfully created an account
    const [accountCreated, setAccountCreated] = useState(false);

    // Close modal when account is successfully created
    useEffect(() => {
        if (!creatingExternalAccount && accountCreated) {
            const timer = setTimeout(() => {
                handleClose();
                setAccountCreated(false);
            }, 1500);

            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creatingExternalAccount, accountCreated]);

    // Get banks/MoMo providers — payout API first, then country-support momo_operators, then legacy
    const getBanksForCountry = useCallback(
        (countryCode: string, methodId?: string): IBankInfo[] => {
            const query = methodId ? payoutBanksQueryForMethod(countryCode, methodId) : null;
            const filterOpts = { ...testModeOpts, methodId };
            if (query && payoutBanksByKey[query.cacheKey]?.length) {
                return filterBanksForPayoutEnvironment(
                    payoutBanksByKey[query.cacheKey].map((b, i) => ({
                        id: b.id ?? String(i),
                        name: b.name,
                        code: b.code,
                    })),
                    countryCode,
                    filterOpts,
                );
            }
            if (
                methodId === 'mobile_money' &&
                countrySupport?.momo_operators &&
                countrySupport.momo_operators.length > 0
            ) {
                return countrySupport.momo_operators.map((m, i) => ({
                    id: m.code || String(i),
                    name: m.name,
                    code: m.code,
                }));
            }
            if (!externalAccountInfo) return [];
            let legacy: IBankInfo[] = [];
            switch (countryCode) {
                case 'NG':
                    legacy = externalAccountInfo.NG || [];
                    break;
                case 'GH':
                    legacy = externalAccountInfo.GH || [];
                    break;
                case 'KE':
                case 'KY':
                    legacy = externalAccountInfo.KY || externalAccountInfo.KE || [];
                    break;
                default:
                    legacy = [];
            }
            return filterBanksForPayoutEnvironment(legacy, countryCode, filterOpts);
        },
        [payoutBanksByKey, externalAccountInfo, testModeOpts, countrySupport],
    );

    const paymentMethods: PaymentMethod[] = useMemo(() => {
        return paymentMethodsForCountrySupport(countrySupport).map(m => ({
            ...m,
            icon: paymentMethodIcon(m.id),
        }));
    }, [countrySupport]);

    // Nigerian users can only create accounts within Nigeria
    const allowedCountries =
        effectiveCountryCode?.toUpperCase() === 'NG' ? countries.filter(c => c.code === 'NG') : countries;

    // Filter countries based on search
    const filteredCountries = allowedCountries.filter(
        country =>
            country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
            country.code.toLowerCase().includes(countrySearch.toLowerCase()),
    );

    const handleCountrySearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCountrySearch(event.target.value);
    };

    const handleCountrySelect = (countryCode: string) => {
        setSelectedCountry(countryCode);
        dispatch(getCountrySupport(countryCode));
        dispatch(clearVerifyPayoutAccount());
        setIsAnimating(true);
        setTimeout(() => {
            setStep('payment');
            setIsAnimating(false);
        }, 300);
    };

    const handlePaymentMethodSelect = (methodId: string) => {
        setSelectedPaymentMethod(methodId);
        dispatch(clearVerifyPayoutAccount());
        if (selectedCountry && shouldLoadPayoutBanksForMethod(methodId)) {
            const bankQuery = payoutBanksQueryForMethod(selectedCountry, methodId);
            // Paystack NG + Flutterwave MoMo/bank corridors
            if (isAutomatedRail(countrySupport) || ['NG', 'GH', 'KE', 'ZA'].includes(selectedCountry)) {
                dispatch(getPayoutBanks(bankQuery));
            }
        }
        if (selectedCountry && (methodId === 'ach' || methodId === 'swift' || methodId === 'bank_account')) {
            dispatch(getStripeConfig(selectedCountry));
        }
        setIsAnimating(true);
        setTimeout(() => {
            setStep('details');
            setIsAnimating(false);
        }, 300);
    };

    const handleBack = () => {
        setIsAnimating(true);
        setTimeout(() => {
            if (step === 'details') {
                setStep('payment');
                setSelectedPaymentMethod('');
                setAccountCreated(false);
            } else if (step === 'payment') {
                setStep('country');
                setSelectedCountry('');
            }
            setIsAnimating(false);
        }, 300);
    };

    const handleClose = () => {
        setStep('country');
        setSelectedCountry('');
        setSelectedPaymentMethod('');
        setIsAnimating(false);
        setFormData({}); // Reset to empty object
        setAccountCreated(false);
        setRoutingBankName(null);
        setRoutingBankLookupLoading(false);
        dispatch(clearVerifyPayoutAccount());
        if (routingLookupTimerRef.current) {
            clearTimeout(routingLookupTimerRef.current);
            routingLookupTimerRef.current = null;
        }
        onClose();
    };

    const invalidateVerification = useCallback(() => {
        dispatch(clearVerifyPayoutAccount());
    }, [dispatch]);

    const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        // Ensure value is always stored as a string to preserve leading zeros
        const inputValue = event.target.value;
        // Explicitly treat as string - never convert to number
        // Use the raw string value directly from the input element
        const stringValue = inputValue === null || inputValue === undefined ? '' : String(inputValue);

        // Double-check: ensure we're not accidentally converting to number
        if (typeof stringValue !== 'string') {
            console.warn(`Field ${field} value is not a string:`, typeof stringValue, stringValue);
        }

        invalidateVerification();
        setFormData((prev: any) => ({ ...prev, [field]: stringValue }));
    };

    const handleAccountHolderNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value.replace(/[^\p{L}\s\-'.]/gu, '');
        invalidateVerification();
        setFormData((prev: any) => ({ ...prev, accountHolderName: raw }));
    };

    const handleDigitsOnlyChange = (field: string, maxLen?: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const digits = event.target.value.replace(/\D/g, '');
        const v = maxLen != null ? digits.slice(0, maxLen) : digits;
        invalidateVerification();
        setFormData((prev: any) => ({ ...prev, [field]: v }));
    };

    const handleSelectChange = (field: string) => (event: any) => {
        invalidateVerification();
        setFormData((prev: any) => ({ ...prev, [field]: event.target.value }));
    };

    const handleSwiftBlur = () => {
        setFormData((prev: any) => ({
            ...prev,
            swiftCode: typeof prev.swiftCode === 'string' ? prev.swiftCode.trim().toUpperCase() : prev.swiftCode,
        }));
    };

    const handleWireAccountNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value.replace(/[^A-Za-z0-9]/g, '');
        invalidateVerification();
        setFormData((prev: any) => ({ ...prev, accountNumber: raw }));
    };

    const wireFieldSx = {
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: theme[themeStyle].form_divider,
            },
            '&:hover fieldset': {
                borderColor: theme[themeStyle].highlight,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme[themeStyle].highlight,
            },
            backgroundColor: theme[themeStyle].form_fields,
            color: theme[themeStyle].tag_white,
        },
        '& .MuiInputLabel-root': {
            color: theme[themeStyle].form_label,
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
        },
        '& .MuiInputBase-input': {
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            color: theme[themeStyle].tag_white,
        },
        '& .MuiInputBase-input::placeholder': {
            color: theme[themeStyle].placeholderGray,
            opacity: 1,
        },
        mb: 1,
    };

    /** ACH: holder name (text), 9-digit valid ABA routing, numeric account #, account type, residential address */
    const achFormValid = useMemo(() => {
        const rn = String(formData.routingNumber ?? '');
        const acct = String(formData.accountNumber ?? '');
        return (
            isValidAccountHolderName(String(formData.accountHolderName ?? '')) &&
            isValidAbaRoutingNumber(rn) &&
            acct.length >= 4 &&
            isDigitsOnlyString(acct) &&
            (formData.accountType === 'checking' || formData.accountType === 'savings') &&
            hasValidResidentialAddress(formData.bankAddress)
        );
    }, [
        formData.accountHolderName,
        formData.routingNumber,
        formData.accountNumber,
        formData.accountType,
        formData.bankAddress,
    ]);

    const banksForForm = useMemo(
        () => getBanksForCountry(selectedCountry, selectedPaymentMethod),
        [getBanksForCountry, selectedCountry, selectedPaymentMethod],
    );

    const handlePaystackTestModeToggle = (enabled: boolean) => {
        setPaystackTestBankMode(enabled);
        writePaystackTestBankModePreference(enabled);
        // Clear bank/MoMo selection when switching Paystack test lists.
        dispatch(clearVerifyPayoutAccount());
        setFormData((prev: any) => ({ ...prev, bankName: '', mobileProvider: '', provider: '' }));
    };

    const formDataTyped = formData as ExternalAccountFormData;

    /** Wire / manual: holder, bank name, SWIFT, account, address, fee ack when required */
    const wireFormValid = useMemo(() => {
        const bank = String(formData.bankName ?? '').trim();
        const addr = String(formData.bankAddress ?? '').trim();
        const acct = String(formData.accountNumber ?? '');
        const swift = String(formData.swiftCode ?? '').trim();
        const feeOk = !countrySupport?.fees?.fee_disclosure_required || Boolean(formData.feeAcknowledged);
        return (
            isValidAccountHolderName(String(formData.accountHolderName ?? '')) &&
            bank.length >= 2 &&
            isSwiftCodeValid(swift) &&
            acct.length >= 4 &&
            /^[A-Za-z0-9]+$/.test(acct) &&
            addr.length >= 4 &&
            feeOk
        );
    }, [
        formData.accountHolderName,
        formData.bankName,
        formData.swiftCode,
        formData.accountNumber,
        formData.bankAddress,
        formData.feeAcknowledged,
        countrySupport?.fees?.fee_disclosure_required,
    ]);

    const detailsStepValid = useMemo(() => {
        let base = false;
        switch (selectedPaymentMethod) {
            case 'ach':
                base = achFormValid;
                break;
            case 'wire':
                base = wireFormValid;
                break;
            case 'swift':
                base =
                    isValidAccountHolderName(String(formData.accountHolderName ?? '')) &&
                    String(formData.accountNumber ?? '').trim().length >= 8;
                break;
            case 'nuban':
                base =
                    isValidAccountHolderName(String(formData.accountHolderName ?? '')) &&
                    String(formData.accountNumber ?? '').trim().length >= 10 &&
                    String(formData.bankName ?? '').trim().length > 0;
                break;
            case 'bank_account':
                if (selectedCountry === 'GB') {
                    base =
                        isValidAccountHolderName(String(formData.accountHolderName ?? '')) &&
                        String(formData.routingNumber ?? '').replace(/\D/g, '').length === 6 &&
                        String(formData.accountNumber ?? '').trim().length >= 6;
                } else if (selectedCountry === 'ZA') {
                    base =
                        isValidAccountHolderName(String(formData.accountHolderName ?? '')) &&
                        String(formData.accountNumber ?? '').trim().length >= 4 &&
                        String(formData.bankName ?? '').trim().length > 0 &&
                        (formData.accountType === 'personal' || formData.accountType === 'business') &&
                        Boolean(formData.documentType) &&
                        Boolean(formData.documentNumber);
                } else {
                    base =
                        isValidAccountHolderName(String(formData.accountHolderName ?? '')) &&
                        String(formData.accountNumber ?? '').trim().length >= 4 &&
                        String(formData.bankName ?? '').trim().length > 0;
                }
                break;
            case 'mobile_money':
                return (
                    isValidAccountHolderName(String(formData.accountHolderName ?? '')) &&
                    String(formData.mobileNumber ?? '').trim().length >= 8 &&
                    String(formData.mobileProvider ?? '').trim().length > 0
                );
            case 'mobile_money_business':
                return (
                    isValidAccountHolderName(String(formData.accountHolderName ?? '')) &&
                    String(formData.mobileNumber ?? '').trim().length >= 4 &&
                    Boolean(formData.momoVariant)
                );
            case 'paypal':
                return Boolean(formData.paypalEmail) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(formData.paypalEmail));
            default:
                return false;
        }
        if (methodNeedsResidentialAddress(selectedPaymentMethod)) {
            return base && hasValidResidentialAddress(formData.bankAddress);
        }
        return base;
    }, [selectedPaymentMethod, selectedCountry, achFormValid, wireFormValid, formData]);

    useEffect(() => {
        if (selectedPaymentMethod !== 'ach') {
            setRoutingBankName(null);
            return;
        }
        const rn = String(formData.routingNumber ?? '');
        if (rn.length !== 9 || !isValidAbaRoutingNumber(rn)) {
            setRoutingBankName(null);
            setRoutingBankLookupLoading(false);
            return;
        }
        if (routingLookupTimerRef.current) clearTimeout(routingLookupTimerRef.current);
        setRoutingBankLookupLoading(true);
        routingLookupTimerRef.current = setTimeout(() => {
            fetchBankNameFromRouting(rn)
                .then(name => {
                    setRoutingBankName(name);
                    if (name) {
                        setFormData((prev: any) => ({ ...prev, bankName: name }));
                    }
                })
                .catch(() => setRoutingBankName(null))
                .finally(() => setRoutingBankLookupLoading(false));
        }, 400);
        return () => {
            if (routingLookupTimerRef.current) clearTimeout(routingLookupTimerRef.current);
        };
    }, [formData.routingNumber, selectedPaymentMethod]);

    const handleVerify = () => {
        const payload = buildVerifyAccountPayload(selectedCountry, selectedPaymentMethod, formDataTyped, banksForForm);
        if (!payload) {
            showToastMessage('Complete required fields before verifying.', 'error');
            return;
        }
        if (
            isPaystackTestBankMethod(selectedCountry, selectedPaymentMethod) &&
            shouldRestrictToPaystackTestBanks(selectedCountry, {
                ...testModeOpts,
                methodId: selectedPaymentMethod,
            })
        ) {
            const bankCode = (payload.bank_details as { bank_code?: string } | undefined)?.bank_code;
            if (!isPaystackTestBankCode(bankCode)) {
                showToastMessage(paystackTestBankHint(), 'error');
                return;
            }
        }
        dispatch(verifyPayoutAccount(payload as any));
    };

    const needsPreSaveVerification = requiresPreSaveAccountVerification(countrySupport, selectedPaymentMethod);
    const accountVerifiedForSave = isVerifyResultAcceptedForSave(verifyResult);

    const handleSubmit = () => {
        if (userExternalAccounts && userExternalAccounts.length >= 3) {
            showToastMessage('Maximum 3 accounts allowed. Please delete an existing account first.', 'error');
            return;
        }

        if (!detailsStepValid) {
            showToastMessage('Please complete all required fields correctly before saving.', 'error');
            return;
        }

        if (needsPreSaveVerification && !accountVerifiedForSave) {
            showToastMessage('Verify the account before saving.', 'error');
            return;
        }

        const payload = buildExternalAccountPayload(
            selectedCountry,
            selectedPaymentMethod,
            formDataTyped,
            banksForForm,
        );

        if (!payload) {
            showToastMessage('Unable to build account payload. Check your selections.', 'error');
            return;
        }

        setAccountCreated(true);
        dispatch(createExternalAccount(payload as any));
    };

    const renderCountrySelection = () => (
        <Box
            sx={{
                opacity: isAnimating ? 0 : 1,
                transform: isAnimating ? 'translateX(20px)' : 'translateX(0)',
                transition: 'all 0.3s ease',
                spaceY: 3,
                pt: { xs: 0.5, sm: 1 },
            }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                    sx={{
                        mx: 'auto',
                        width: 48,
                        height: 48,
                        bgcolor: theme[themeStyle].darkGray60,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                    }}>
                    <LocationOn sx={{ color: theme[themeStyle].highlight, fontSize: 24 }} />
                </Box>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: theme[themeStyle].tag_white,
                    }}>
                    Select Your Country
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: theme[themeStyle].light_gray_text,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                    }}>
                    Choose your country to see available payment options
                </Typography>
            </Box>

            {/* Search Input */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search countries..."
                    value={countrySearch}
                    onChange={handleCountrySearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: theme[themeStyle].highlight }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: theme[themeStyle].form_divider,
                            },
                            '&:hover fieldset': {
                                borderColor: theme[themeStyle].highlight,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: theme[themeStyle].highlight,
                            },
                            backgroundColor: theme[themeStyle].form_fields,
                            color: theme[themeStyle].tag_white,
                        },
                        '& .MuiInputBase-input': {
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            color: theme[themeStyle].tag_white,
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: theme[themeStyle].placeholderGray,
                            opacity: 1,
                        },
                    }}
                />
            </Box>

            <Box
                sx={{
                    height: 320,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                {filteredCountries.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 4,
                            color: theme[themeStyle].light_gray_text,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <Typography variant="body2">No countries found matching "{countrySearch}"</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={1} sx={{ flex: 1 }}>
                        {filteredCountries.map(country => (
                            <Grid item xs={6} sm={4} key={country.code}>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        backgroundColor: theme[themeStyle].secondaryBg,
                                        border: `1px solid ${theme[themeStyle].selectableBorder}`,
                                        '&:hover': {
                                            boxShadow: `0px 4px 8px ${theme[themeStyle].shadowColor}`,
                                            transform: 'scale(1.05)',
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    }}
                                    onClick={() => handleCountrySelect(country.code)}>
                                    <CardContent sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="h4" sx={{ mb: 1 }}>
                                            {country.flag}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                fontSize: '0.75rem',
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: theme[themeStyle].tag_white,
                                            }}>
                                            {country.name}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Box>
    );

    const renderPaymentMethodSelection = () => {
        const selectedCountryData = countries.find(c => c.code === selectedCountry);

        return (
            <Box
                sx={{
                    opacity: isAnimating ? 0 : 1,
                    transform: isAnimating ? 'translateX(20px)' : 'translateX(0)',
                    transition: 'all 0.3s ease',
                    spaceY: 3,
                    pt: { xs: 0.5, sm: 1 },
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <IconButton
                        onClick={handleBack}
                        size="small"
                        sx={{
                            color: theme[themeStyle].tag_white,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].button_background,
                            },
                        }}>
                        <ArrowBack />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h5">{selectedCountryData?.flag}</Typography>
                        <Chip
                            label={selectedCountryData?.name}
                            variant="outlined"
                            size="small"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                borderColor: theme[themeStyle].highlight,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}
                        />
                    </Box>
                </Box>

                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                        sx={{
                            mx: 'auto',
                            width: 48,
                            height: 48,
                            bgcolor: theme[themeStyle].secondaryBg,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                        }}>
                        <AccountBalance sx={{ color: theme[themeStyle].highlight, fontSize: 24 }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            mb: 1,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: theme[themeStyle].tag_white,
                        }}>
                        Choose Payment Method
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme[themeStyle].light_gray_text,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        Select how you'd like to receive payments
                    </Typography>
                </Box>

                <Box sx={{ spaceY: 2 }}>
                    {paymentMethods.length === 0 ? (
                        <Typography sx={{ textAlign: 'center', color: theme[themeStyle].light_gray_text, py: 2 }}>
                            Loading payment options…
                        </Typography>
                    ) : (
                        paymentMethods.map(method => (
                            <Card
                                key={method.id}
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    backgroundColor: theme[themeStyle].secondaryBg,
                                    border: `1px solid ${theme[themeStyle].selectableBorder}`,
                                    '&:hover': {
                                        boxShadow: `0px 4px 8px ${theme[themeStyle].shadowColor}`,
                                        transform: 'scale(1.02)',
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    mb: 1,
                                }}
                                onClick={() => handlePaymentMethodSelect(method.id)}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ color: theme[themeStyle].highlight }}>{method.icon}</Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: 500,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: theme[themeStyle].tag_white,
                                                }}>
                                                {method.name}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme[themeStyle].light_gray_text,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                }}>
                                                {method.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </Box>
            </Box>
        );
    };

    const renderDetailsForm = () => {
        const selectedCountryData = countries.find(c => c.code === selectedCountry);
        const selectedMethod = paymentMethods.find(m => m.id === selectedPaymentMethod);
        const banksLoading = Boolean(
            loadingPayoutBanksKey &&
                payoutBanksQueryForMethod(selectedCountry, selectedPaymentMethod).cacheKey === loadingPayoutBanksKey,
        );

        return (
            <Box
                sx={{
                    opacity: isAnimating ? 0 : 1,
                    transform: isAnimating ? 'translateX(20px)' : 'translateX(0)',
                    transition: 'all 0.3s ease',
                    spaceY: 3,
                    pt: { xs: 0.5, sm: 1 },
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <IconButton
                        onClick={handleBack}
                        size="small"
                        sx={{
                            color: theme[themeStyle].tag_white,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].button_background,
                            },
                        }}>
                        <ArrowBack />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h5">{selectedCountryData?.flag}</Typography>
                        <Chip
                            label={selectedMethod?.name}
                            variant="outlined"
                            size="small"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                borderColor: theme[themeStyle].highlight,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}
                        />
                    </Box>
                </Box>

                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                        sx={{
                            mx: 'auto',
                            width: 48,
                            height: 48,
                            bgcolor: theme[themeStyle].secondaryBg,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                        }}>
                        <Person sx={{ color: theme[themeStyle].highlight, fontSize: 24 }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            mb: 1,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].tag_white,
                        }}>
                        Account Details
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme[themeStyle].light_gray_text,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        Enter your account information
                    </Typography>
                </Box>

                {isPaystackTestBankMethod(selectedCountry, selectedPaymentMethod) && !isLivePayoutBankEnvironment() && (
                    <Box sx={{ mb: 2 }}>
                        {showTestBankToggle && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    mb: 1.5,
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme[themeStyle].highlight, 0.25)}`,
                                    bgcolor: alpha(theme[themeStyle].highlight, 0.06),
                                }}>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].tag_white,
                                            fontSize: 13,
                                        }}>
                                        Paystack test bank mode
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].light_gray_text,
                                            fontSize: 11,
                                        }}>
                                        Admin only · non-production. Off = live bank list (may hit resolve limits).
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={paystackTestBankMode}
                                            onChange={(_, checked) => handlePaystackTestModeToggle(checked)}
                                            color="warning"
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: 12,
                                                color: theme[themeStyle].tag_white,
                                            }}>
                                            {paystackTestBankMode ? 'Test' : 'Live'}
                                        </Typography>
                                    }
                                    labelPlacement="start"
                                    sx={{ m: 0 }}
                                />
                            </Box>
                        )}
                        {shouldRestrictToPaystackTestBanks(selectedCountry, {
                            ...testModeOpts,
                            methodId: selectedPaymentMethod,
                        }) && (
                            <Alert severity="info">
                                {paystackTestBankHint()} Use account number <strong>0000000000</strong> with bank code{' '}
                                <strong>001</strong> for successful test verifies.
                            </Alert>
                        )}
                        {showTestBankToggle && !paystackTestBankMode && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                Live banks enabled. Paystack test mode may reject live bank resolves or hit the daily
                                limit.
                            </Alert>
                        )}
                    </Box>
                )}

                {selectedPaymentMethod === 'mobile_money' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {flutterwaveMomoHint(selectedCountry)}
                    </Alert>
                )}

                {isManualWireRail(countrySupport) && (
                    <Box
                        sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(theme[themeStyle].highlight, 0.08),
                            border: `1px solid ${alpha(theme[themeStyle].highlight, 0.25)}`,
                        }}>
                        <Typography
                            sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: theme[themeStyle].highlight, mb: 0.5 }}>
                            Manual international bank transfer — not instant
                        </Typography>
                        {countrySupport?.timeline?.label && (
                            <Typography
                                variant="body2"
                                sx={{ color: theme[themeStyle].light_gray_text, fontFamily: FONT_ENUM.MAIN_REGULAR }}>
                                {countrySupport.timeline.label}
                            </Typography>
                        )}
                        {countrySupport?.fees?.client_copy && (
                            <Typography
                                variant="caption"
                                sx={{ display: 'block', mt: 1, color: theme[themeStyle].light_gray_text }}>
                                {countrySupport.fees.client_copy}
                            </Typography>
                        )}
                    </Box>
                )}

                {accountVerifiedForSave && (
                    <Typography sx={{ mb: 2, color: theme[themeStyle].highlight, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        {verifyResult?.verified_account_name
                            ? `Verified account name: ${verifyResult.verified_account_name}`
                            : 'Account verified — you can save.'}
                    </Typography>
                )}

                <Box component="form" sx={{ spaceY: 3, flexDirection: 'column', gap: 2 }}>
                    {/* Common fields */}
                    <TextField
                        label="Account Holder Name"
                        fullWidth
                        placeholder="Enter full name as on account"
                        value={formData.accountHolderName || ''}
                        onChange={handleAccountHolderNameChange}
                        error={
                            Boolean(formData.accountHolderName) &&
                            !isValidAccountHolderName(String(formData.accountHolderName))
                        }
                        helperText={
                            formData.accountHolderName && !isValidAccountHolderName(String(formData.accountHolderName))
                                ? 'Use letters only (spaces, hyphens, apostrophes allowed)'
                                : undefined
                        }
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: theme[themeStyle].form_divider,
                                },
                                '&:hover fieldset': {
                                    borderColor: theme[themeStyle].highlight,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: theme[themeStyle].highlight,
                                },
                                backgroundColor: theme[themeStyle].form_fields,
                                color: theme[themeStyle].tag_white,
                            },
                            '& .MuiInputLabel-root': {
                                color: theme[themeStyle].form_label,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                            '& .MuiInputBase-input': {
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                color: theme[themeStyle].tag_white,
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: theme[themeStyle].placeholderGray,
                                opacity: 1,
                            },
                            mb: 1,
                        }}
                    />

                    {/* US ACH Transfer */}
                    {selectedPaymentMethod === 'ach' && (
                        <>
                            <TextField
                                label="Routing Number"
                                fullWidth
                                type="text"
                                placeholder="9-digit routing number"
                                value={formData.routingNumber ?? ''}
                                onChange={handleDigitsOnlyChange('routingNumber', 9)}
                                inputProps={{
                                    inputMode: 'numeric',
                                    maxLength: 9,
                                    style: { textAlign: 'left' },
                                }}
                                error={
                                    Boolean(formData.routingNumber) &&
                                    String(formData.routingNumber).length === 9 &&
                                    !isValidAbaRoutingNumber(String(formData.routingNumber))
                                }
                                helperText={
                                    String(formData.routingNumber ?? '').length === 9 &&
                                    !isValidAbaRoutingNumber(String(formData.routingNumber))
                                        ? 'Routing number is not valid (check digit)'
                                        : undefined
                                }
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                    mb: 1,
                                }}
                            />
                            {(routingBankLookupLoading || routingBankName) && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        mb: 1,
                                        ml: 0.5,
                                        color: theme[themeStyle].light_gray_text,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    }}>
                                    {routingBankLookupLoading
                                        ? 'Looking up bank…'
                                        : routingBankName
                                        ? `Bank: ${routingBankName}`
                                        : null}
                                </Typography>
                            )}
                            <TextField
                                label="Account Number"
                                fullWidth
                                type="text"
                                placeholder="Account number (digits only)"
                                value={formData.accountNumber ?? ''}
                                onChange={handleDigitsOnlyChange('accountNumber')}
                                inputProps={{
                                    inputMode: 'numeric',
                                    style: { textAlign: 'left' },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                    mb: 1,
                                }}
                            />
                            <FormControl fullWidth>
                                <InputLabel
                                    shrink
                                    sx={{
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Account Type
                                </InputLabel>
                                <Select
                                    value={formData.accountType || ''}
                                    onChange={handleSelectChange('accountType')}
                                    label="Account Type"
                                    error={!formData.accountType}
                                    displayEmpty
                                    notched
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].button_background,
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                    }}>
                                    <MenuItem value="" disabled>
                                        <em>Select account type</em>
                                    </MenuItem>
                                    <MenuItem value="checking">Checking</MenuItem>
                                    <MenuItem value="savings">Savings</MenuItem>
                                </Select>
                            </FormControl>
                        </>
                    )}

                    {/* Nigerian NUBAN Transfer */}
                    {selectedPaymentMethod === 'nuban' && (
                        <>
                            <FormControl fullWidth>
                                <InputLabel
                                    sx={{
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Bank Name
                                </InputLabel>
                                <Select
                                    value={formData.bankName || ''}
                                    onChange={handleSelectChange('bankName')}
                                    label="Bank Name"
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].button_background,
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                        mb: 1,
                                    }}>
                                    {banksLoading || gettingExternalAccountInfo ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Loading banks...
                                        </MenuItem>
                                    ) : (
                                        banksForForm.map(bank => (
                                            <MenuItem key={bank.id} value={bank.name}>
                                                {bank.name}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Account Number"
                                fullWidth
                                type="text"
                                placeholder="10-digit NUBAN account number"
                                inputProps={{
                                    maxLength: 10,
                                    inputMode: 'text',
                                    style: { textAlign: 'left' },
                                }}
                                value={formData.accountNumber ?? ''}
                                onChange={handleInputChange('accountNumber')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                }}
                            />
                        </>
                    )}

                    {/* Ghanaian Mobile Money */}
                    {selectedPaymentMethod === 'mobile_money' && (
                        <>
                            <FormControl fullWidth>
                                <InputLabel
                                    sx={{
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Mobile Money Provider
                                </InputLabel>
                                <Select
                                    value={formData.mobileProvider || ''}
                                    onChange={handleSelectChange('mobileProvider')}
                                    label="Mobile Money Provider"
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].button_background,
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                        mb: 1,
                                    }}>
                                    {gettingExternalAccountInfo ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Loading providers...
                                        </MenuItem>
                                    ) : (
                                        banksForForm.map(provider => (
                                            <MenuItem key={provider.id} value={provider.name}>
                                                {provider.name}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Mobile Number"
                                fullWidth
                                type="text"
                                placeholder="Mobile money number"
                                value={formData.mobileNumber ?? ''}
                                onChange={handleInputChange('mobileNumber')}
                                inputProps={{
                                    inputMode: 'text',
                                    style: { textAlign: 'left' },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                    mb: 1,
                                }}
                            />
                            <TextField
                                label="Account Name"
                                fullWidth
                                placeholder="Name registered with mobile money"
                                value={formData.accountName || ''}
                                onChange={handleInputChange('accountName')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].form_divider,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].form_label,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: theme[themeStyle].placeholderGray,
                                        opacity: 1,
                                    },
                                }}
                            />
                        </>
                    )}

                    {/* South African bank + validation fields */}
                    {selectedPaymentMethod === 'bank_account' && selectedCountry === 'ZA' && (
                        <>
                            <FormControl fullWidth>
                                <InputLabel
                                    sx={{ color: theme[themeStyle].form_label, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                    Bank Name
                                </InputLabel>
                                <Select
                                    value={formData.bankName || ''}
                                    onChange={handleSelectChange('bankName')}
                                    label="Bank Name"
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                '& .MuiMenuItem-root': { color: theme[themeStyle].tag_white },
                                            },
                                        },
                                    }}
                                    sx={{
                                        mb: 1,
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    }}>
                                    {banksLoading ? (
                                        <MenuItem disabled>Loading banks…</MenuItem>
                                    ) : (
                                        banksForForm.map(bank => (
                                            <MenuItem key={bank.code} value={bank.name}>
                                                {bank.name}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Account Number"
                                fullWidth
                                value={formData.accountNumber ?? ''}
                                onChange={handleInputChange('accountNumber')}
                                sx={wireFieldSx}
                            />
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <InputLabel sx={{ color: theme[themeStyle].form_label }}>Account type</InputLabel>
                                <Select
                                    value={formData.accountType || ''}
                                    onChange={handleSelectChange('accountType')}
                                    label="Account type"
                                    sx={{
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    }}>
                                    <MenuItem value="personal">Personal</MenuItem>
                                    <MenuItem value="business">Business</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <InputLabel sx={{ color: theme[themeStyle].form_label }}>Document type</InputLabel>
                                <Select
                                    value={formData.documentType || ''}
                                    onChange={handleSelectChange('documentType')}
                                    label="Document type"
                                    sx={{
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    }}>
                                    <MenuItem value="identityNumber">SA ID number</MenuItem>
                                    <MenuItem value="passportNumber">Passport</MenuItem>
                                    <MenuItem value="businessRegistrationNumber">Business registration</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Document number"
                                fullWidth
                                value={formData.documentNumber ?? ''}
                                onChange={handleInputChange('documentNumber')}
                                sx={wireFieldSx}
                            />
                        </>
                    )}

                    {/* Ghana / Kenya bank account */}
                    {selectedPaymentMethod === 'bank_account' &&
                        (selectedCountry === 'GH' || selectedCountry === 'KE') && (
                            <>
                                <FormControl fullWidth>
                                    <InputLabel
                                        sx={{ color: theme[themeStyle].form_label, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                        Bank Name
                                    </InputLabel>
                                    <Select
                                        value={formData.bankName || ''}
                                        onChange={handleSelectChange('bankName')}
                                        label="Bank Name"
                                        sx={{
                                            mb: 1,
                                            backgroundColor: theme[themeStyle].form_fields,
                                            color: theme[themeStyle].tag_white,
                                        }}>
                                        {banksForForm.map(bank => (
                                            <MenuItem key={bank.code} value={bank.name}>
                                                {bank.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Account Number"
                                    fullWidth
                                    value={formData.accountNumber ?? ''}
                                    onChange={handleInputChange('accountNumber')}
                                    sx={wireFieldSx}
                                />
                            </>
                        )}

                    {/* Default bank_account (all other countries) */}
                    {selectedPaymentMethod === 'bank_account' &&
                        !['ZA', 'GH', 'KE', 'GB'].includes(selectedCountry) && (
                        <>
                            <FormControl fullWidth>
                                <InputLabel
                                    sx={{ color: theme[themeStyle].form_label, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                    Bank Name
                                </InputLabel>
                                <Select
                                    value={formData.bankName || ''}
                                    onChange={handleSelectChange('bankName')}
                                    label="Bank Name"
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    '&:hover': { backgroundColor: theme[themeStyle].button_background },
                                                },
                                            },
                                        },
                                    }}
                                    sx={{
                                        mb: 1,
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: theme[themeStyle].form_divider },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme[themeStyle].highlight },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme[themeStyle].highlight },
                                        '& .MuiSvgIcon-root': { color: theme[themeStyle].tag_white },
                                    }}>
                                    {banksLoading || gettingExternalAccountInfo ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Loading banks...
                                        </MenuItem>
                                    ) : banksForForm.length > 0 ? (
                                        banksForForm.map(bank => (
                                            <MenuItem key={bank.id} value={bank.name}>
                                                {bank.name}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled value="">
                                            <em>No banks available — type name below</em>
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                            {banksForForm.length === 0 && !banksLoading && (
                                <TextField
                                    label="Bank Name"
                                    fullWidth
                                    placeholder="Enter your bank name"
                                    value={formData.bankName || ''}
                                    onChange={handleInputChange('bankName')}
                                    sx={wireFieldSx}
                                />
                            )}
                            <TextField
                                label="Account Number"
                                fullWidth
                                type="text"
                                placeholder="Account number"
                                value={formData.accountNumber ?? ''}
                                onChange={handleInputChange('accountNumber')}
                                inputProps={{ inputMode: 'text', style: { textAlign: 'left' } }}
                                sx={wireFieldSx}
                            />
                        </>
                    )}

                    {/* UK sort code + account */}
                    {selectedPaymentMethod === 'bank_account' && selectedCountry === 'GB' && (
                        <>
                            <TextField
                                label="Sort code (6 digits)"
                                fullWidth
                                value={formData.routingNumber ?? ''}
                                onChange={handleDigitsOnlyChange('routingNumber', 6)}
                                sx={wireFieldSx}
                            />
                            <TextField
                                label="Account number"
                                fullWidth
                                value={formData.accountNumber ?? ''}
                                onChange={handleInputChange('accountNumber')}
                                sx={wireFieldSx}
                            />
                        </>
                    )}

                    {/* EU IBAN (Stripe) */}
                    {selectedPaymentMethod === 'swift' && (
                        <TextField
                            label="IBAN"
                            fullWidth
                            placeholder="Full IBAN"
                            value={formData.accountNumber ?? ''}
                            onChange={handleWireAccountNumberChange}
                            sx={wireFieldSx}
                        />
                    )}

                    {/* Kenya M-Pesa business */}
                    {selectedPaymentMethod === 'mobile_money_business' && (
                        <>
                            <FormControl fullWidth sx={{ mb: 1 }}>
                                <InputLabel sx={{ color: theme[themeStyle].form_label }}>M-Pesa type</InputLabel>
                                <Select
                                    value={formData.momoVariant || ''}
                                    onChange={handleSelectChange('momoVariant')}
                                    label="M-Pesa type"
                                    sx={{
                                        backgroundColor: theme[themeStyle].form_fields,
                                        color: theme[themeStyle].tag_white,
                                    }}>
                                    <MenuItem value="paybill">Paybill</MenuItem>
                                    <MenuItem value="till">Till</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label={formData.momoVariant === 'till' ? 'Till number' : 'Paybill number'}
                                fullWidth
                                value={formData.mobileNumber ?? ''}
                                onChange={handleInputChange('mobileNumber')}
                                sx={wireFieldSx}
                            />
                        </>
                    )}

                    {/* PayPal */}
                    {selectedPaymentMethod === 'paypal' && (
                        <>
                            <TextField
                                label="PayPal email"
                                fullWidth
                                type="email"
                                value={formData.paypalEmail ?? ''}
                                onChange={handleInputChange('paypalEmail')}
                                sx={wireFieldSx}
                            />
                            <TextField
                                label="Display name (optional)"
                                fullWidth
                                value={formData.paypalUsername ?? ''}
                                onChange={handleInputChange('paypalUsername')}
                                sx={wireFieldSx}
                            />
                        </>
                    )}

                    {/* Wire Transfer (common for all) */}
                    {selectedPaymentMethod === 'wire' && (
                        <>
                            <TextField
                                label="Bank Name"
                                fullWidth
                                placeholder="Full bank name"
                                value={formData.bankName || ''}
                                onChange={handleInputChange('bankName')}
                                sx={wireFieldSx}
                            />
                            <TextField
                                label="SWIFT/BIC Code"
                                fullWidth
                                type="text"
                                placeholder="8–11 characters (letters and numbers)"
                                value={formData.swiftCode || ''}
                                onChange={handleInputChange('swiftCode')}
                                onBlur={handleSwiftBlur}
                                error={Boolean(formData.swiftCode) && !isSwiftCodeValid(String(formData.swiftCode))}
                                helperText={
                                    formData.swiftCode && !isSwiftCodeValid(String(formData.swiftCode))
                                        ? 'SWIFT/BIC must be 8–11 letters or numbers'
                                        : undefined
                                }
                                sx={wireFieldSx}
                            />
                            <TextField
                                label="Account Number"
                                fullWidth
                                type="text"
                                placeholder="Account number (letters and numbers only)"
                                value={formData.accountNumber ?? ''}
                                onChange={handleWireAccountNumberChange}
                                inputProps={{
                                    inputMode: 'text',
                                    style: { textAlign: 'left' },
                                }}
                                sx={wireFieldSx}
                            />
                            {countrySupport?.fees?.fee_disclosure_required && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={Boolean(formData.feeAcknowledged)}
                                            onChange={(_, checked) =>
                                                setFormData((prev: any) => ({ ...prev, feeAcknowledged: checked }))
                                            }
                                            sx={{
                                                color: theme[themeStyle].highlight,
                                                '&.Mui-checked': { color: theme[themeStyle].highlight },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                color: theme[themeStyle].light_gray_text,
                                                fontSize: 14,
                                            }}>
                                            I understand that manual transfer fees may apply and my bank may receive
                                            less than the requested amount.
                                        </Typography>
                                    }
                                />
                            )}
                        </>
                    )}

                    {methodNeedsResidentialAddress(selectedPaymentMethod) && (
                        <ResidentialAddressField
                            value={String(formData.bankAddress || '')}
                            onChange={address => {
                                invalidateVerification();
                                setFormData((prev: any) => ({ ...prev, bankAddress: address }));
                            }}
                            fieldSx={wireFieldSx}
                            highlightColor={theme[themeStyle].highlight}
                            labelColor={theme[themeStyle].form_label}
                            mutedColor={theme[themeStyle].light_gray_text}
                            surfaceColor={theme[themeStyle].secondaryBg}
                            hoverBgColor={theme[themeStyle].button_background}
                            textColor={theme[themeStyle].tag_white}
                        />
                    )}

                    <Box sx={{ pt: 2, spaceY: 2 }}>
                        {needsPreSaveVerification && (
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={handleVerify}
                                disabled={!detailsStepValid || verifyingAccount || accountVerifiedForSave}
                                sx={{
                                    borderColor: theme[themeStyle].highlight,
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    mb: 1,
                                }}>
                                {verifyingAccount ? (
                                    <>
                                        <CircularProgress size={18} sx={{ mr: 1 }} />
                                        Verifying…
                                    </>
                                ) : accountVerifiedForSave ? (
                                    'Account verified'
                                ) : (
                                    'Verify account before save'
                                )}
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={handleSubmit}
                            disabled={
                                !detailsStepValid ||
                                creatingExternalAccount ||
                                (needsPreSaveVerification && !accountVerifiedForSave)
                            }
                            sx={{
                                backgroundColor: theme[themeStyle].highlight,
                                color: theme[themeStyle].black,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '&:hover': {
                                    backgroundColor: alpha(theme[themeStyle].highlight, 0.88),
                                },
                                '&:disabled': {
                                    backgroundColor: theme[themeStyle].form_divider,
                                    color: theme[themeStyle].placeholderGray,
                                },
                                mb: 1,
                            }}>
                            {creatingExternalAccount ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: theme[themeStyle].black }} />
                                    Creating Account...
                                </>
                            ) : (
                                'Save Account Details'
                            )}
                        </Button>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleClose}
                            sx={{
                                borderColor: theme[themeStyle].highlight,
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '&:hover': {
                                    borderColor: theme[themeStyle].highlight,
                                    backgroundColor: theme[themeStyle].button_background,
                                },
                            }}>
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={device.isMobile}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: theme[themeStyle].deep_grey_lighter,
                    backgroundImage: 'none',
                    minHeight: device.isMobile ? '100vh' : 600,
                    maxHeight: device.isMobile ? '100vh' : '90vh',
                },
            }}>
            <DialogTitle
                sx={{
                    textAlign: 'center',
                    color: theme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    borderBottom: `1px solid ${theme[themeStyle].form_divider}`,
                    position: 'relative',
                    pr: 6,
                    pt: { xs: 2.5, sm: 3 },
                    pb: { xs: 2, sm: 2.5 },
                }}>
                Account Setup
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: theme[themeStyle].tag_white,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].button_background,
                        },
                    }}>
                    <CloseIcon
                        fill={theme[themeStyle].tag_white}
                        stroke={theme[themeStyle].tag_white}
                        width="20px"
                        height="20px"
                    />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    pt: { xs: 4, sm: 5 },
                    px: { xs: 2.5, sm: 3.5 },
                    pb: { xs: 4, sm: 4 },
                    backgroundColor: theme[themeStyle].form_background,
                    overflowY: 'auto',
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: theme[themeStyle].highlight,
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme[themeStyle].highlight,
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme[themeStyle].highlight, 0.55),
                    },
                }}>
                {step === 'country' && renderCountrySelection()}
                {step === 'payment' && renderPaymentMethodSelection()}
                {step === 'details' && renderDetailsForm()}
            </DialogContent>
        </Dialog>
    );
};

export default ExternalAccountModal;
